import { create } from "zustand";
import {
	ActiveListingsEnum,
	BasementFilterEnum,
	ListingCard,
	OpenHouseEnum,
} from "../services/exploreListings";

interface SortFilter {
	page: string;
	limit: string;
	sortBy: string;
	sortOrder: string;
}

interface SelectedOption {
	value: string;
	displayValue: string;
}

interface TogglesPropertyType {
	active: boolean;
	sold: boolean;
	de_listed: boolean;
}
interface MapFiltersState {
	openFilterPanel: boolean;
	setOpenFilterPanel: (openFilterPanel: boolean) => void;
	propertyPrice: number[];
	setPropertyPrice: (price: number[]) => void;

	propertySquareFootage: number[];
	setPropertySquareFootage: (propertySquareFootage: number[]) => void;

	propertyMaintenanceFee: number[];
	setPropertyMaintenanceFee: (propertyMaintenanceFee: number[]) => void;

	beds: string[];
	setBeds: (beds: string[]) => void;

	mapSelectedCities: string[];
	setMapSelectedCities: (city: string) => void;
	removeMapSelectedCity: (city: string) => void;

	baths: string[];
	setBaths: (baths: string[]) => void;

	sortFilter: SortFilter;
	setSortFilter: (update: Partial<SortFilter>) => void;

	parking: string[];
	setParking: (parking: string[]) => void;

	reloadMapCities: boolean;
	setReloadMapCities: (reload: boolean) => void;

	dens: boolean | null;
	setDens: (dens: boolean | null) => void;

	propertyType: string[];
	setPropertyType: (updateFn: (prevTypes: string[]) => string[]) => void;
	propertyTypeLocal: string[];
	setPropertyTypeLocal: (updateFn: (prevTypes: string[]) => string[]) => void;

	minPrice: number;
	setMinPrice: (minPrice: number) => void;

	maxPrice: number;
	setMaxPrice: (maxPrice: number) => void;

	daysOnMarket: string | null;
	setDaysOnMarket: (daysOnMarket: string | null) => void;

	ageOfProperty: string | null;
	setAgeOfProperty: (ageOfProperty: string | null) => void;

	style: string | null;
	setStyle: (style: string | null) => void;

	basement: string | null;
	setBasement: (basement: string | null) => void;

	sqft: string | null;
	setSqft: (sqft: string | null) => void;

	garageType: string | null;
	setGarageType: (garageType: string | null) => void;

	openHouseType: string | null;
	setOpenHouseType: (openHouseType: string | null) => void;

	numberOfKitchens: string | null;
	setNumberOfKitchens: (numberOfKitchens: string | null) => void;

	clearAllFilters: () => void;
	getAllFilters: () => MapFiltersState;
	keywordSearch: string | null;
	setKeywordSearch: (keywordSearch: string | null) => void;

	selectedOptions: {
		active: SelectedOption;
		sold: SelectedOption;
		de_listed: SelectedOption;
	};
	setSelectedOption: (
		label: keyof MapFiltersState["selectedOptions"],
		option: SelectedOption,
	) => void;
	selectedOptionMultiple: { [key: string]: string[] };
	setSelectedOptionMultiple: (label: string, option: string) => void;

	togglesPropertyType: Record<string, boolean>;
	togglePropertyTypeButton: (propertyType: string) => void;
	setTogglesPropertyType: (newState: TogglesPropertyType) => void;

	openActiveFilter: boolean;
	setOpenActiveFilter: (
		updater: (openActiveFilter: boolean) => boolean,
	) => void;
	openSoldFilter: boolean;
	setOpenSoldFilter: (updater: (openSoldFilter: boolean) => boolean) => void;

	sortValue: string;
	setSortValue: (value: string) => void;
	switchMode: "list" | "map";
	setSwitchMode: (mode: "list" | "map") => void;

	isDropdownOpen: boolean;
	setIsDropdownOpen: (isDropdownOpen: boolean) => void;
}

export const useMapFiltersStore = create<MapFiltersState>((set, get) => ({
	switchMode: "map",
	setSwitchMode: (mode) => set({ switchMode: mode }),
	sortValue: "date",
	setSortValue: (value: string) => set({ sortValue: value }),
	openActiveFilter: true,
	setOpenActiveFilter: (updater) =>
		set((state) => ({
			openActiveFilter: updater(state.openActiveFilter),
		})),
	isDropdownOpen: false,
	setIsDropdownOpen: (isDropdownOpen) => set({ isDropdownOpen: isDropdownOpen }),
	openSoldFilter: false,
	setOpenSoldFilter: (updater) =>
		set((state) => ({ openSoldFilter: updater(state.openSoldFilter) })),
	openFilterPanel: false,
	setOpenFilterPanel: (filterPanel) => set({ openFilterPanel: filterPanel }),
	togglesPropertyType: {
		active: true,
		sold: false,
		de_listed: false,
	},
	togglePropertyTypeButton: (propertyType) =>
		set((state) => ({
			togglesPropertyType: {
				...state.togglesPropertyType,
				[propertyType]: !state.togglesPropertyType[propertyType],
			},
		})),
	setTogglesPropertyType: (newState) =>
		set(() => ({
			togglesPropertyType: {
				...newState,
			},
		})),

	selectedOptionMultiple: {},

	setSelectedOptionMultiple: (label: string, option: string) =>
		set((state) => {
			const currentOptions = state.selectedOptionMultiple[label] || [];

			const isSelected = currentOptions.includes(option);

			const updatedOptions = isSelected
				? currentOptions.filter((item) => item !== option)
				: [...currentOptions, option];

			return {
				selectedOptionMultiple: {
					...state.selectedOptionMultiple,
					[label]: updatedOptions,
				},
			};
		}),

	selectedOptions: {
		active: { value: "All", displayValue: "All" },
		sold: { value: "WITHIN_007_DAYS", displayValue: "7D" },
		de_listed: { value: "WITHIN_090_DAYS", displayValue: "90D" },
	},

	setSelectedOption: (label, option) =>
		set((state) => ({
			selectedOptions: {
				...state.selectedOptions,
				[label]: option,
			},
		})),
	propertyPrice: [0, 5000000],
	setPropertyPrice: (price) => set({ propertyPrice: price }),

	propertySquareFootage: [0, 4000],
	setPropertySquareFootage: (price) => set({ propertySquareFootage: price }),

	propertyMaintenanceFee: [0, 1500],
	setPropertyMaintenanceFee: (price) => set({ propertyMaintenanceFee: price }),

	beds: ["All"],
	setBeds: (beds) => set({ beds }),

	keywordSearch: null,
	setKeywordSearch: (keywordSearch) => set({ keywordSearch }),

	baths: ["All"],
	setBaths: (baths) => set({ baths }),

	parking: ["All"],
	setParking: (parking) => set({ parking }),

	dens: null,
	setDens: (dens) => set({ dens }),

	propertyType: ["All types"],
	setPropertyType: (updateFn) =>
		set((state) => ({ propertyType: updateFn(state.propertyType ?? []) })),

	propertyTypeLocal: ["All types"],
	setPropertyTypeLocal: (updateFn) =>
		set((state) => ({
			propertyTypeLocal: updateFn(state.propertyTypeLocal ?? []),
		})),

	minPrice: 0,
	setMinPrice: (minPrice) => set({ minPrice }),

	maxPrice: 2000000,
	setMaxPrice: (maxPrice) => set({ maxPrice }),

	daysOnMarket: null,
	setDaysOnMarket: (daysOnMarket) => set({ daysOnMarket }),

	reloadMapCities: false,
	setReloadMapCities: (reload) => set({ reloadMapCities: reload }),

	ageOfProperty: null,
	setAgeOfProperty: (ageOfProperty) => set({ ageOfProperty }),

	mapSelectedCities: [] as string[],
	setMapSelectedCities: (city: string) =>
		set((state) => ({
			mapSelectedCities: state.mapSelectedCities.includes(city)
				? state.mapSelectedCities
				: [...state.mapSelectedCities, city],
		})),

	removeMapSelectedCity: (city: string) =>
		set((state) => ({
			mapSelectedCities: state.mapSelectedCities.filter((c) => c !== city),
		})),

	style: null,
	setStyle: (style) => set({ style }),

	basement: null,
	setBasement: (basement) => set({ basement }),

	sqft: null,
	setSqft: (sqft) => set({ sqft }),

	garageType: null,
	setGarageType: (garageType) => set({ garageType }),

	openHouseType: null,
	setOpenHouseType: (openHouseType) => set({ openHouseType }),

	numberOfKitchens: null,
	setNumberOfKitchens: (numberOfKitchens) => set({ numberOfKitchens }),

	sortFilter: {
		page: "1",
		limit: "100",
		sortBy: "date",
		sortOrder: "desc",
	},
	setSortFilter: (update) =>
		set((state) => ({
			sortFilter: { ...state.sortFilter, ...update },
		})),

	clearAllFilters: () =>
		set({
			togglesPropertyType: {
				active: true,
				sold: false,
				de_listed: false,
			},
			selectedOptions: {
				active: { value: "All", displayValue: "All" },
				sold: { value: "WITHIN_007_DAYS", displayValue: "7D" },
				de_listed: { value: "WITHIN_090_DAYS", displayValue: "90D" },
			},
			openActiveFilter: true,
			openSoldFilter: false,
			openHouseType: null,
			keywordSearch: null,
			beds: ["All"],
			baths: ["All"],
			parking: ["All"],
			dens: null,
			propertyType: ["All types"],
			propertyPrice: [0, 5000000],
			propertySquareFootage: [0, 4000],
			propertyMaintenanceFee: [0, 1500],
			minPrice: 0,
			maxPrice: 2000000,
			daysOnMarket: null,
			ageOfProperty: null,
			style: null,
			basement: null,
			sqft: null,
			garageType: null,
			numberOfKitchens: null,
			mapSelectedCities: [],
			sortFilter: {
				page: "1",
				limit: "100",
				sortBy: "date",
				sortOrder: "desc",
			},
		}),

	getAllFilters: () => get(),
}));
